'use client';
import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getBaseImageUrl, getMerchantHref } from '@/constants/hooks';

interface Props {
    merchants: any[];
    companyDomain: string;
    mer_slug: string;
    mer_slug_type: string;
}

const RoundedMerchantSlider: React.FC<Props> = ({ merchants, companyDomain, mer_slug, mer_slug_type }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showSliders, setShowSliders] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollWidth, clientWidth } = scrollContainerRef.current;
                setShowSliders(scrollWidth > clientWidth);
            }
        };

        checkScroll();
        const timeout = setTimeout(checkScroll, 500); 
        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', checkScroll);
        }
    }, [merchants]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = container.clientWidth * 0.75;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    if (!merchants || merchants.length === 0) return null;

    return (
        <div className="relative group/slider w-full px-10 sm:px-12">
            
            {/* Slider Buttons with Savylo Colors */}
            {showSliders && (
                <>
                    <button 
                        onClick={() => scroll('left')}
                        className="absolute left-0 top-[40%] -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-[0_4px_15px_rgba(30,58,95,0.1)] border border-[#E5E7EB] text-[#1E3A5F] hover:bg-[#F97316] hover:text-white hover:border-[#F97316] transition-all opacity-0 group-hover/slider:opacity-100"
                        aria-label="Scroll left"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <button 
                        onClick={() => scroll('right')}
                        className="absolute right-0 top-[40%] -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-[0_4px_15px_rgba(30,58,95,0.1)] border border-[#E5E7EB] text-[#1E3A5F] hover:bg-[#F97316] hover:text-white hover:border-[#F97316] transition-all opacity-0 group-hover/slider:opacity-100"
                        aria-label="Scroll right"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>
                </>
            )}

            {/* Scrollable Container */}
            <div 
                ref={scrollContainerRef}
                className="flex overflow-x-auto gap-4 sm:gap-5 md:gap-6 pb-4 px-2 -mx-2 scroll-smooth snap-x snap-mandatory"
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                <style jsx>{`
                    div::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>
                
                {merchants.map((merchant, i) => {
                    const merchantName = merchant.merchant_name;
                    const merchantLogo = merchant.merchant_logo;
                    const cashbackAmount = merchant?.discount_tag;
                    const href = getMerchantHref(merchant, mer_slug, mer_slug_type);

                    return (
                        <Link
                            key={i}
                            href={href}
                            className="group flex flex-col items-center w-[90px] sm:w-[100px] md:w-[110px] no-underline shrink-0 snap-start"
                        >
                            {/* Circle Container with Savylo Colors */}
                            <div className="w-[90px] h-[90px] sm:w-[100px] sm:h-[100px] md:w-[110px] md:h-[110px] rounded-full border-2 border-[#E5E7EB] bg-white flex items-center justify-center p-4 mb-3 group-hover:border-[#F97316] group-hover:shadow-[0_0_20px_rgba(249,115,22,0.2)] transition-all duration-300 relative overflow-hidden">
                                {/* Gradient background on hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-[#F8FAFC] to-[#FFF7ED] opacity-0 group-hover:opacity-30 transition-opacity duration-300 rounded-full" />
                                
                                {merchantLogo ? (
                                    <div className="relative w-full h-full z-10">
                                        <Image
                                            src={getBaseImageUrl(companyDomain, merchantLogo, '')}
                                            alt={merchantName || 'Merchant'}
                                            fill
                                            className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                                            sizes="(max-width: 768px) 90px, 110px"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#1E3A5F] font-bold text-xl uppercase z-10">
                                        {merchantName?.[0]}
                                    </div>
                                )}
                            </div>
                            
                            {/* Merchant Name & Cashback */}
                            <div className="flex flex-col items-center justify-center gap-0.5 w-full px-1">
                                <span className="text-[#4B5563] font-medium text-xs text-center line-clamp-2 leading-tight group-hover:text-[#111827] transition-colors duration-300 font-sans">
                                    {merchantName}
                                </span>
                                {cashbackAmount && (
                                    <span className="text-[10px] font-bold text-[#DC2626] font-sans">
                                        {cashbackAmount}% Back
                                    </span>
                                )}
                            </div>
                        </Link>
                    )
                })}
            </div>
        </div>
    );
};

export default RoundedMerchantSlider;
